import React from 'react';

import {ClientEvent, Tracker} from '@udemy/event-tracking';
import {IconName} from '@udemy/icons';

import {POP_QUIZ_SETTINGS, PopQuizSetting} from 'src/lib/pop-quiz/pop-quiz-settings';

import {FREQUENCY_CAP_MS, LONG_SNOOZE_DAYS, MS_PER_DAY, UDEMY_LOGO_CDN_URL} from './constants';
import {PopQuizLectureType, PopQuizRelatedLecture, QuizData} from './types';

type LogoProps = {
    src?: string;
    [key: string]: unknown;
};

export function renderUdemyLogo(
    Component: React.ComponentType<LogoProps>,
    props: LogoProps,
): React.ReactElement {
    return <Component {...props} src={UDEMY_LOGO_CDN_URL} />;
}

/**
 * True when a debug-forced Pop Quiz session's side effects (tracking events, attempt mutations)
 * Suppresses both events and mutations in prod environment due to data pollution concerns.
 */
export function isDebugQuizSuppressed(quizData: QuizData): boolean {
    return Boolean(quizData.isDebugQuiz) && process.env.DEPLOY_ENV === 'prod';
}

export function publishPopQuizEvent(event: ClientEvent, quizData: QuizData): void {
    if (!isDebugQuizSuppressed(quizData)) {
        Tracker.publishEvent(event);
    }
}

/**
 * Returns true when the pop quiz should NOT be shown — either because the
 * long snooze is still active or the frequency cap has been reached.
 */
export function isCapped(getPopQuizSetting: (s: PopQuizSetting) => string): boolean {
    if (typeof window === 'undefined') {
        // Settings are not available server-side — treat as capped so the query stays client-only.
        return true;
    }
    // Long snooze takes priority: check if snoozedAt + 60 days is still in the future
    const snoozedAt = getPopQuizSetting(POP_QUIZ_SETTINGS.snoozedAt);
    if (snoozedAt) {
        const snoozeExpiresAt = new Date(snoozedAt).getTime() + LONG_SNOOZE_DAYS * MS_PER_DAY;
        if (Date.now() < snoozeExpiresAt) {
            return true;
        }
    }
    // Fall through to frequency cap
    const lastShown = getPopQuizSetting(POP_QUIZ_SETTINGS.lastShownDate);
    if (!lastShown) {
        return false;
    }
    return Date.now() - new Date(lastShown).getTime() < FREQUENCY_CAP_MS;
}

const LECTURE_TYPENAME_TO_TYPE: Record<string, PopQuizLectureType> = {
    VideoLecture: 'video',
    ArticleLecture: 'article',
    PresentationLecture: 'presentation',
    VideoMashupLecture: 'video_mashup',
    AudioLecture: 'audio',
    FileLecture: 'file',
    EBookLecture: 'file',
    ExternalLecture: 'external_link',
};

export function mapLectureTypename(typename: string): PopQuizLectureType {
    return LECTURE_TYPENAME_TO_TYPE[typename] ?? 'missing_content';
}

interface RawRelatedLecture {
    __typename: string;
    id: string;
    title: string;
    urlLanding: string;
}

// Shared by both the MultipleChoiceQuizItem and MultiSelectQuizItem branches of the
// supportedItems mapper — `relatedLectures` is undefined when queried with
// includeRelatedLectures: false (i.e. popQuizV2ExperienceEnabled is off).
export function mapRelatedLectures(
    lectures: RawRelatedLecture[] | undefined,
): PopQuizRelatedLecture[] {
    return (lectures ?? []).map((lecture) => ({
        id: lecture.id,
        title: lecture.title,
        url: lecture.urlLanding,
        type: mapLectureTypename(lecture.__typename),
    }));
}

// Icon glyph shown on the lecture link CTA, per lecture content type.
const LECTURE_TYPE_TO_ICON_GLYPH: Record<PopQuizLectureType, IconName> = {
    video: 'video',
    article: 'article',
    presentation: 'presentation',
    video_mashup: 'video-mashup',
    audio: 'audio',
    file: 'download-file',
    external_link: 'link',
    missing_content: 'blank',
};

export function mapLectureTypeToIconGlyph(type: PopQuizLectureType): IconName {
    return LECTURE_TYPE_TO_ICON_GLYPH[type];
}

/**
 * Converts a GraphQL `ID` (always a string) to the numeric id schema-store's event fields
 * expect. Returns `null` instead of `NaN` when the id isn't numeric, so callers can skip firing
 * an event with a bad id rather than publish `NaN`.
 */
export function toEventId(id: string): number | null {
    const numericId = Number(id);
    return Number.isFinite(numericId) ? numericId : null;
}
